import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Filter, Bookmark, Clock, DollarSign, RefreshCw, Loader2, MapPin, X, Landmark } from "lucide-react";
import { toast } from "sonner";
import { SearchableSelect } from "@/components/SearchableSelect";

const fmtMoney = (n) => n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const daysUntil = (iso) => {
  if (!iso) return null;
  const diff = Math.ceil((new Date(iso) - new Date()) / (1000 * 60 * 60 * 24));
  return diff;
};

const SCOPES = [
  { value: "all", label: "All grants" },
  { value: "federal", label: "Federal only" },
  { value: "state", label: "State & local" },
];

export default function Grants() {
  const [grants, setGrants] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [state, setState] = useState("");
  const [county, setCounty] = useState("");
  const [scope, setScope] = useState("all");
  const [categories, setCategories] = useState([]);
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [countiesLoading, setCountiesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());

  const fetchGrants = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (cat && cat !== "all") params.set("category", cat);
      if (state) params.set("state", state);
      if (county) params.set("county", county);
      if (scope && scope !== "all") params.set("scope", scope);
      params.set("limit", "60");
      const res = await api.get(`/grants?${params.toString()}`);
      setGrants(res.data.grants || []);
      setTotal(res.data.total || 0);
    } finally {
      setLoading(false);
    }
  };

  const fetchSaved = async () => {
    try {
      const res = await api.get("/grants/saved/list");
      setSavedIds(new Set((res.data.grants || []).map((g) => g.id)));
    } catch (err) {
      // Non-critical: saved list is a UI-only bookmark set. Show a subtle hint so
      // the user knows the "saved" state may be out of sync, but don't spam errors.
      if (err?.response?.status && err.response.status !== 401) {
        toast.error("Couldn't load your saved grants.");
      }
    }
  };

  useEffect(() => {
    fetchGrants();
    fetchSaved();
    api.get("/grants/categories").then((r) => setCategories(r.data.categories || []));
    api.get("/locations/states").then((r) => setStates(r.data.states || []));
    // Mount-only fetch; fetchGrants/fetchSaved are stable in-scope closures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload counties whenever state changes; clear the previously-selected county.
  useEffect(() => {
    setCounty("");
    if (!state) { setCounties([]); return; }
    setCountiesLoading(true);
    api
      .get(`/locations/states/${state}/counties`)
      .then((r) => setCounties(r.data.counties || []))
      .catch(() => setCounties([]))
      .finally(() => setCountiesLoading(false));
  }, [state]);

  useEffect(() => {
    fetchGrants();
    // Reactive triggers: state, county, scope, cat. fetchGrants is a stable in-scope
    // closure whose only reactive inputs are already in this list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, county, scope, cat]);

  const onSearch = (e) => {
    e.preventDefault();
    fetchGrants();
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await api.post("/grants/refresh");
      toast.success(`Refreshed. ${res.data.added} new grants added.`);
      fetchGrants();
    } catch (err) {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const clearFilters = () => {
    setQ(""); setCat("all"); setState(""); setCounty(""); setScope("all");
  };

  const toggleSave = async (grantId) => {
    if (savedIds.has(grantId)) {
      await api.delete(`/grants/${grantId}/save`);
      const s = new Set(savedIds); s.delete(grantId); setSavedIds(s);
      toast.success("Removed from saved");
    } else {
      await api.post(`/grants/${grantId}/save`);
      setSavedIds(new Set([...savedIds, grantId]));
      toast.success("Saved");
    }
  };

  const stateOptions = states.map((s) => ({
    value: s.code,
    label: s.name,
    hint: s.county_count > 1 ? `${s.county_count} counties` : "",
  }));
  const countyOptions = counties.map((c) => ({ value: c, label: c }));
  const activeFilterCount = [
    q, cat !== "all" ? cat : null, state || null, county || null, scope !== "all" ? scope : null
  ].filter(Boolean).length;

  const renderGrantsSection = () => {
    if (loading) {
      return <div className="text-center py-24 text-[#5E5E5E]"><Loader2 className="w-6 h-6 animate-spin inline"/></div>;
    }
    if (grants.length === 0) {
      return (
        <div className="bg-white border border-[#E6E4DF] rounded-lg py-24 text-center">
          <div className="text-[#5E5E5E]">No grants match those filters.</div>
          <button onClick={clearFilters} className="mt-3 text-sm text-[#1C3F35] underline" data-testid="grants-empty-clear">
            Clear filters
          </button>
        </div>
      );
    }
    return (
      <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="grants-list">
        {grants.map((g) => {
          const d = daysUntil(g.deadline);
          const isSaved = savedIds.has(g.id);
          return (
            <li key={g.id} className="bg-white border border-[#E6E4DF] rounded-lg p-6 flex flex-col hover:shadow-md transition-shadow" data-testid={`grant-card-${g.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs uppercase tracking-widest text-[#7A8C77] font-semibold">{g.category || "Grant"}</span>
                  {g.state && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#C1654B]/10 text-[#C1654B] font-bold uppercase tracking-widest" data-testid={`state-badge-${g.id}`}>
                      {g.state}{g.county ? ` · ${g.county}` : ""}
                    </span>
                  )}
                </div>
                <button
                  onClick={()=>toggleSave(g.id)}
                  data-testid={`save-toggle-${g.id}`}
                  className={`p-1.5 rounded-md transition-colors ${isSaved ? "bg-[#1C3F35] text-white" : "bg-[#F9F8F6] text-[#5E5E5E] hover:bg-[#E8ECEB]"}`}
                  aria-label={isSaved ? "Unsave" : "Save"}
                >
                  <Bookmark className="w-4 h-4" fill={isSaved ? "currentColor" : "none"}/>
                </button>
              </div>
              <Link to={`/grants/${g.id}`} className="mt-3 font-heading text-lg font-medium text-[#1A1A1A] hover:text-[#1C3F35] line-clamp-2 leading-snug">
                {g.title}
              </Link>
              <div className="mt-2 text-sm text-[#5E5E5E] line-clamp-2">{g.description?.slice(0,180)}</div>

              <div className="mt-auto pt-6 flex items-center justify-between text-xs text-[#5E5E5E]">
                <span className="font-medium text-[#1A1A1A] truncate max-w-[45%]">{g.agency}</span>
                <div className="flex items-center gap-3">
                  {(g.funding_max || g.funding_min) && (
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-3 h-3"/>
                      {fmtMoney(g.funding_max || g.funding_min)}
                    </span>
                  )}
                  {g.deadline && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3"/>
                      {d != null && d > 0 ? `${d}d` : "open"}
                    </span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="space-y-8" data-testid="grants-page">
      <header className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="label-eyebrow mb-2">Discover</div>
          <h1 className="font-heading font-light text-4xl md:text-5xl tracking-tighter">Find your next grant</h1>
          <p className="text-[#5E5E5E] mt-2">{total.toLocaleString()} live opportunities from Grants.gov, state agencies & county programs.</p>
        </div>
        <Button
          onClick={onRefresh}
          disabled={refreshing}
          data-testid="grants-refresh"
          variant="outline"
          className="rounded-md border-[#E6E4DF]"
        >
          {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <RefreshCw className="w-4 h-4 mr-2"/>}
          Refresh from Grants.gov
        </Button>
      </header>

      {/* Scope tabs */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="grants-scope-tabs">
        {SCOPES.map((s) => (
          <button
            key={s.value}
            onClick={() => setScope(s.value)}
            data-testid={`scope-tab-${s.value}`}
            className={`px-4 py-2 rounded-md text-sm transition-colors ${
              scope === s.value
                ? "bg-[#1C3F35] text-white"
                : "bg-white border border-[#E6E4DF] text-[#1A1A1A] hover:bg-[#E8ECEB]"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <form onSubmit={onSearch} className="bg-white border border-[#E6E4DF] rounded-lg p-4 space-y-3" data-testid="grants-search-bar">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5E5E5E]" />
            <Input
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              data-testid="grants-search-input"
              placeholder="Search by keyword, agency, or program"
              className="pl-10 bg-white border-[#E6E4DF]"
            />
          </div>
          <Button type="submit" data-testid="grants-search-submit" className="bg-[#1C3F35] text-white hover:bg-[#1A362D]">
            Search
          </Button>
        </div>
        <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-[#E6E4DF]">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#7A8C77] font-bold">
            <Filter className="w-3.5 h-3.5"/> Filters
          </div>

          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="w-full sm:w-[180px] bg-white border-[#E6E4DF]" data-testid="grants-category-select">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>

          <div className="w-full sm:w-[220px]">
            <SearchableSelect
              value={state}
              onChange={setState}
              options={stateOptions}
              placeholder="All states & territories"
              emptyLabel="No matching state."
              testId="grants-state-select"
              icon={MapPin}
            />
          </div>

          <div className="w-full sm:w-[220px]">
            <SearchableSelect
              value={county}
              onChange={setCounty}
              options={countyOptions}
              placeholder={countiesLoading ? "Loading…" : "County (optional)"}
              emptyLabel="No matching county."
              disabled={!state || countiesLoading}
              disabledHint="Pick a state first"
              testId="grants-county-select"
              icon={Landmark}
            />
          </div>

          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              data-testid="grants-clear-filters"
              className="text-xs text-[#5E5E5E] hover:text-[#C1654B] inline-flex items-center gap-1 ml-auto"
            >
              <X className="w-3 h-3"/>Clear filters ({activeFilterCount})
            </button>
          )}
        </div>
      </form>

      {renderGrantsSection()}
    </div>
  );
}
